/**
 * Lifecycle → hub sections grouping (specs/competitions-hub-client AC-1.1).
 * Pure. Unknown lifecycle values are grouped under Past (safe default — the
 * archive is the only section where a stale/unknown state cannot mislead).
 */

import type { CompetitionSummary } from '../types/competitions.types';

export type HubSectionKey = 'live' | 'upcoming' | 'past';

export interface HubSection {
  key: HubSectionKey;
  competitions: CompetitionSummary[];
}

export function groupCompetitionsIntoSections(
  competitions: CompetitionSummary[],
): HubSection[] {
  const buckets: Record<HubSectionKey, CompetitionSummary[]> = {
    live: [],
    upcoming: [],
    past: [],
  };

  for (const competition of competitions) {
    if (competition.lifecycle === 'LIVE') buckets.live.push(competition);
    else if (competition.lifecycle === 'UPCOMING') buckets.upcoming.push(competition);
    else buckets.past.push(competition);
  }

  // The future reads soonest-first; live and past keep the server's newest-first order.
  buckets.upcoming.sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
  );

  return (['live', 'upcoming', 'past'] as const)
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({ key, competitions: buckets[key] }));
}

/**
 * The hub's hero: the first LIVE competition, else the soonest UPCOMING one.
 * Null when only past competitions exist (hero row is skipped).
 */
export function pickHeroCompetition(
  competitions: CompetitionSummary[],
): CompetitionSummary | null {
  const sections = groupCompetitionsIntoSections(competitions);
  const live = sections.find((s) => s.key === 'live');
  if (live) return live.competitions[0];
  const upcoming = sections.find((s) => s.key === 'upcoming');
  if (upcoming) return upcoming.competitions[0];
  return null;
}

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
}

/** Non-negative D/H/M until the given ISO date; null once it has passed (or on bad input). */
export function countdownParts(startDateIso: string, nowMs: number): CountdownParts | null {
  const target = new Date(startDateIso).getTime();
  if (!Number.isFinite(target)) return null;
  const diff = target - nowMs;
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
  };
}
